const CHALLENGE_CODE = "delivery_group_capacity_split_confirmation_required";
const formatTons = (value) => `${new Intl.NumberFormat("fr-CA", { maximumFractionDigits: 3 }).format(value)}\u00a0t`;
const identifiers = (values) => Array.isArray(values) ? values.map((value) => String(value).trim()).filter(Boolean) : [];
const sameIds = (left, right) => left.length === right.length && new Set(left).size === left.length
  && left.every((id) => right.includes(id));

export function createDeliveryGroupCapacitySplitChallengeHandler({
  dismissConfirmation, expectedDeliveryIds = [], retryMutation, setConfirmationDialog,
  targetTruckId, targetTruckLabel,
} = {}) {
  return ({ payload, requestBody, response } = {}) => {
    if (response?.status !== 409 || payload?.code !== CHALLENGE_CODE
      || typeof retryMutation !== "function" || typeof setConfirmationDialog !== "function") return false;
    const details = payload.error_details;
    if (!details || !requestBody || requestBody.confirm_capacity_split === true
      || !details.confirmation_id || !details.runtime_revision
      || details.target_truck_id !== targetTruckId || requestBody.target_truck_id !== targetTruckId
      || !sameIds(identifiers(details.delivery_ids), identifiers(expectedDeliveryIds))
      || !sameIds(identifiers(requestBody.delivery_ids), identifiers(expectedDeliveryIds))
      || !Array.isArray(details.loads) || details.loads.length !== 2
      || details.loads.some((load) => !Array.isArray(load.orders) || !load.orders.length
        || !Number.isFinite(load.quantity_tons) || load.quantity_tons <= 0)) return false;

    const retryBody = {
      ...requestBody,
      confirm_capacity_split: true,
      capacity_split_confirmation_id: details.confirmation_id,
      expected_runtime_revision: details.runtime_revision,
    };
    const visits = Array.isArray(details.additional_visits) ? details.additional_visits : [];
    const summaryItems = details.loads.map((load, index) => ({
      label: `Chargement ${index + 1} · ${formatTons(load.quantity_tons)}`,
      value: load.orders.map((order) => `${order.order_id} (${formatTons(order.quantity_tons)})`).join(" · "),
      multiline: true,
    }));
    if (visits.length) summaryItems.push({
      label: "Visites supplémentaires",
      value: visits.map((visit) => `${visit.client_label || "Client"} : ${visit.after_visit_count} passages`).join(" · "),
      multiline: true,
    });
    let settled = false;
    const close = () => {
      settled = true;
      if (typeof dismissConfirmation === "function") dismissConfirmation();
      else setConfirmationDialog(null);
    };
    setConfirmationDialog({
      isOpen: true,
      title: "Diviser en deux chargements ?",
      description: `${targetTruckLabel || details.target_truck_label || targetTruckId} · Capacité ${formatTons(details.target_capacity_tons)}. Toutes les commandes seront réaffectées en deux chargements successifs.`,
      confirmLabel: "Diviser et réaffecter",
      cancelLabel: "Annuler",
      tone: "accent",
      cornerRadius: "grain",
      panelSize: "compact",
      contentDensity: "compact",
      summaryDensity: "compact",
      summaryLayout: "stack",
      summaryItems,
      warningMessage: details.protect_after_reassignment !== false
        ? "Protection activée : Optimiser conservera les deux chargements sur ce camion."
        : "Protection désactivée : un prochain clic sur Optimiser pourra modifier ces chargements et leurs affectations.",
      onCancel: close,
      onClose: close,
      onDismiss: close,
      onConfirm: () => {
        if (settled) return;
        close();
        retryMutation({ requestBody: retryBody, requestExpectedRuntimeRevision: details.runtime_revision });
      },
    });
    return true;
  };
}
